import { z } from 'zod';
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Center,
  Checkbox,
  Flex,
  HStack,
  Icon,
  Input,
  ListItem,
  Spinner,
  Badge,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import { useCallback, ChangeEvent, useState, useMemo, useRef } from 'react';
import { useDebounce } from 'use-debounce';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ChevronRightIcon, UserPlus, Users, FilePlus2, Download } from 'lucide-react';
import capitalize from 'lodash-es/capitalize.js';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { ProviderUserRoleColorSchemes } from '@duke-hq/shared-constants/providers';
import { useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';
import { useProviderEmailDomains } from '../../hooks/useProviderEmailDomains';
import {
  useBulkInternalEnrolments,
  InternalEnrolmentMode,
  type User,
  type BulkEnrolmentMember,
} from '@duke-hq/app-provider-web/tenant/components/BulkEnrolments/useBulkInternalEnrolments';
import { ConsultantRoleSchema } from '@duke-hq/svc-api/provider/util/consultants';
import { CsvImporter } from '@duke-hq/csv-parser/components/CsvImporter';
import { CsvDownloaderHandler } from '@duke-hq/csv-parser/components/CsvDownloaderHandler';
import { CsvRecord, ValidationRules } from '@duke-hq/csv-parser/CsvTypes';
import { defaultValidationRules } from '@duke-hq/csv-parser/CsvUtils';
import {
  TeamIndividualUserForm,
  TeamIndividualUserFormRef,
} from '@duke-hq/app-provider-web/tenant/Team/TeamBulkUsers/TeamIndividualUserForm';

const UserSchema = z.object({
  id: z.string().or(z.number()).optional(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.email(),
  role: z.string().optional(),
});

const getProviderEmailDomain = (email: string, allowedDomain: string) => {
  return `${email}@${allowedDomain}`;
};

const SAMPLE_CSV_TEMPLATE = ['john.smith', 'jane.taylor', 'michael.jones', 'susan.miller'];

function UserRow({ user }: { user: User }) {
  const { selectedMembers } = useBulkInternalEnrolments();
  const isChecked = useMemo(
    () => selectedMembers.some((m) => m.id === user.id),
    [selectedMembers, user.id],
  );

  return (
    <HStack justify="space-between" w="full">
      <Flex alignItems="center" justifyContent="center">
        <Checkbox mr="12px" isChecked={isChecked} pointerEvents="none" />
        <Avatar size="sm" mr="12px" name={`${user.first_name} ${user.last_name}`} />
        <Box>
          <Text fontSize="sm" lineHeight="1.5" fontWeight="medium">
            {`${user.first_name} ${user.last_name}`}
          </Text>
          <Text fontSize="xs" lineHeight="1.5" fontWeight="normal" color="fg.muted">
            {user.email}
          </Text>
        </Box>
      </Flex>
      {user.role ? (
        <Badge
          borderRadius="full"
          colorScheme={ProviderUserRoleColorSchemes[user.role] ?? 'gray'}
          variant="muted"
          textTransform="none"
        >
          {user.role.replace(/_/g, ' ')}
        </Badge>
      ) : null}
    </HStack>
  );
}

function UserList({ users }: { users: User[] | undefined }) {
  const { selectedMembers, setSelectedMembers } = useBulkInternalEnrolments();

  const toggleSelectedMember = useCallback(
    (user: User) => {
      const member: BulkEnrolmentMember = {
        id: user.id,
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        role: user.role,
      };

      const exists = selectedMembers.some((m) => m.id === member.id);

      if (exists) {
        setSelectedMembers(selectedMembers.filter((m) => m.id !== member.id));
      } else {
        setSelectedMembers([
          ...selectedMembers,
          {
            ...member,
            isExistingUser: true,
          },
        ]);
      }
    },
    [selectedMembers, setSelectedMembers],
  );

  if (!users) {
    return (
      <Box w="full">
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>Sorry, we couldn't load users. Please refresh or try again later.</Trans>
        </Text>
      </Box>
    );
  }

  if (users.length === 0) {
    return (
      <Box w="full">
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>No users found.</Trans>
        </Text>
      </Box>
    );
  }

  return (
    <UnorderedList
      styleType="none"
      w="full"
      p="0"
      m="0"
      border="1px solid"
      borderRadius="8px"
      borderColor="border.emphasis"
      maxH="400px"
      overflowY="auto"
      sx={{
        'li:not(:last-child)': {
          borderBottom: '1px solid',
          borderColor: 'border.emphasis',
        },
        'li:first-of-type': {
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
        },
        'li:last-of-type': {
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
        },
      }}
    >
      {users?.map((user) => {
        const isChecked = selectedMembers.some((m) => m.id === user.id);
        return (
          <ListItem
            key={user.id}
            onClick={() => toggleSelectedMember(user)}
            p="8px 16px"
            bg={isChecked ? 'bg.muted' : 'transparent'}
            cursor="pointer"
            aria-checked={isChecked}
          >
            <UserRow user={user} />
          </ListItem>
        );
      })}
    </UnorderedList>
  );
}

function TeamMemberModeHeader({
  member,
  mode,
  onModeChange,
}: {
  member?: NonNullable<RouterOutputs['users']['consultantById']>;
  mode: InternalEnrolmentMode;
  onModeChange: (newMode: InternalEnrolmentMode) => void;
}) {
  return (
    <HStack justifyContent="space-between" w="full">
      <Text size="md" fontWeight="bold">
        {member ? t`Update team member` : t`Invite team member`}
      </Text>

      <HStack spacing={2}>
        <ButtonGroup
          alignItems="center"
          bg="bg.muted"
          borderRadius="md"
          size="xs"
          spacing={0}
          p={1}
          overflow="hidden"
        >
          <Button
            onClick={() => onModeChange(InternalEnrolmentMode.EXISTING)}
            leftIcon={<Icon as={Users} boxSize="16px" />}
            variant="ghost"
            bg={mode === InternalEnrolmentMode.EXISTING ? 'white' : 'transparent'}
            color={mode === InternalEnrolmentMode.EXISTING ? 'brand.600' : 'fg.muted'}
            borderRadius="md"
            width="fit-content"
            _hover={{
              bg: mode === InternalEnrolmentMode.EXISTING ? 'white' : 'gray.100',
            }}
          >
            <Trans>Existing users</Trans>
          </Button>

          <Button
            onClick={() => onModeChange(InternalEnrolmentMode.NEW)}
            leftIcon={<Icon as={UserPlus} boxSize="16px" />}
            variant="ghost"
            bg={mode === InternalEnrolmentMode.NEW ? 'white' : 'transparent'}
            color={mode === InternalEnrolmentMode.NEW ? 'brand.600' : 'fg.muted'}
            borderRadius="md"
            _hover={{
              bg: mode === InternalEnrolmentMode.NEW ? 'white' : 'gray.100',
            }}
            w="fit-content"
          >
            <Trans>New members</Trans>
          </Button>
        </ButtonGroup>
      </HStack>
    </HStack>
  );
}

export function SelectMembers({ goToNextStep }: { goToNextStep: () => void }) {
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 500);
  const formRef = useRef<TeamIndividualUserFormRef>(null);

  const auth = useAuthTenant();
  const { allowedDomains, defaultDomain } = useProviderEmailDomains();

  const [showCsvUploader, setShowCsvUploader] = useState(false);
  const [isIndividualFormSubmitting, setIsIndividualFormSubmitting] = useState(false);
  const [isIndividualFormLoading, setIsIndividualFormLoading] = useState(false);
  const [isIndividualFormValid, setIsIndividualFormValid] = useState(false);
  const [totalRows, setTotalRows] = useState<{
    validRows: number;
    errorsRows: number;
    isValidated: boolean;
  }>({
    validRows: 0,
    errorsRows: 0,
    isValidated: false,
  });

  const { mode, setMode, selectedMembers, setSelectedMembers } = useBulkInternalEnrolments();
  const { data: usersList, isLoading: isLoadingUsers } = trpcTenant.users.all.useQuery({
    search: debouncedSearch,
  });

  const sampleCsv = useMemo<{ first_name: string; last_name: string; email: string }[]>(() => {
    if (defaultDomain == null) {
      return [];
    }

    return SAMPLE_CSV_TEMPLATE.map((username) => {
      const [first_name = '', last_name = ''] = username.split('.');
      return {
        first_name: capitalize(first_name),
        last_name: capitalize(last_name),
        email: getProviderEmailDomain(username, defaultDomain),
      };
    });
  }, [defaultDomain]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const q = value.trim();
    setSearch(q);
  };

  const handleProcessedCsvData = useCallback(
    (data: CsvRecord[]) => {
      const importedMembers: BulkEnrolmentMember[] = [];
      const usersByEmail = new Map<string, User>();

      if (usersList instanceof Array) {
        usersList.forEach((user) => {
          if (user?.email != null && user?.id != null) {
            const cleanedUser: User = {
              id: user.id,
              first_name: user.first_name ?? '',
              last_name: user.last_name ?? '',
              email: user.email,
            };

            usersByEmail.set(user.email.toLowerCase(), cleanedUser);
          }
        });
      }

      data.forEach((csvRow) => {
        const parsed = UserSchema.safeParse(csvRow);
        if (!parsed.success) {
          return;
        }

        const consultant = parsed.data;
        const lowerCaseEmail = consultant.email.toLowerCase();
        const matchingUser = usersByEmail.get(lowerCaseEmail);

        importedMembers.push({
          id: matchingUser?.id ?? undefined,
          firstName: matchingUser?.first_name ?? consultant.first_name,
          lastName: matchingUser?.last_name ?? consultant.last_name,
          email: matchingUser?.email ?? consultant.email,
          isExistingUser: !!matchingUser,
        });
      });

      setSelectedMembers(importedMembers);
    },
    [usersList, setSelectedMembers],
  );

  const handleCsvUploader = useCallback(() => {
    setSelectedMembers([]);
    setShowCsvUploader(true);
    setMode(InternalEnrolmentMode.BULK);

    if (formRef.current) {
      formRef.current.resetForm();
      setIsIndividualFormValid(false);
    }
  }, [setSelectedMembers, setShowCsvUploader, setMode, formRef, setIsIndividualFormValid]);

  const selectMembersValidationRules: ValidationRules = useMemo(
    () => ({
      first_name: defaultValidationRules.first_name,
      last_name: defaultValidationRules.last_name,
      email: defaultValidationRules.email,
    }),
    [],
  );

  const selectMembersColumnDisplayNames = useMemo(
    () => ({
      first_name: 'First name',
      last_name: 'Last name',
      email: 'Email',
    }),
    [],
  );

  const handleIndividualFormStateChange = useCallback(
    (submitting: boolean, loading: boolean, isValid: boolean) => {
      setIsIndividualFormSubmitting(submitting);
      setIsIndividualFormLoading(loading);
      setIsIndividualFormValid(isValid);
    },
    [],
  );

  const handleGoToNextStep = useCallback(async () => {
    if (mode === InternalEnrolmentMode.BULK && showCsvUploader) {
      if (selectedMembers.length > 0) {
        goToNextStep();
      }

      return;
    }

    if (mode === InternalEnrolmentMode.NEW) {
      if (formRef.current) {
        const formData = await formRef.current.getValidatedData();
        if (formData) {
          const newMember: BulkEnrolmentMember = {
            id: formData.id,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            role: formData.role ?? undefined,
            site: formData.site ?? undefined,
            region: formData.region ?? undefined,
            providerId: auth?.user.tenant?.id ?? null,
            isExistingUser: false,
          };

          setSelectedMembers([newMember]);
          goToNextStep();
        }
      }
    }

    if (selectedMembers.length > 0) {
      goToNextStep();
    }
  }, [
    showCsvUploader,
    mode,
    formRef,
    selectedMembers,
    goToNextStep,
    setSelectedMembers,
    auth?.user.tenant?.id,
  ]);

  const handleTotalRows = useCallback(
    ({
      validRows,
      errorsRows,
      isValidated,
    }: {
      validRows: number;
      errorsRows: number;
      isValidated: boolean;
    }) => {
      setTotalRows({
        validRows,
        errorsRows,
        isValidated: (validRows > 0 && errorsRows === 0) ?? isValidated,
      });
    },
    [],
  );

  const canGoToNextStep = useMemo(() => {
    if (showCsvUploader) {
      return selectedMembers.length > 0 && totalRows.isValidated;
    } else if (mode === InternalEnrolmentMode.NEW) {
      return isIndividualFormValid && !isIndividualFormSubmitting && !isIndividualFormLoading;
    }

    return selectedMembers.length > 0;
  }, [
    mode,
    showCsvUploader,
    selectedMembers.length,
    totalRows.isValidated,
    isIndividualFormValid,
    isIndividualFormSubmitting,
    isIndividualFormLoading,
  ]);

  const onModeChange = useCallback(
    (newMode: InternalEnrolmentMode) => {
      setMode(newMode);
      setShowCsvUploader(false);
      setSelectedMembers([]);
      if (formRef.current) {
        formRef.current.resetForm();
        setIsIndividualFormValid(false);
      }
    },
    [setMode, setShowCsvUploader, setSelectedMembers, formRef, setIsIndividualFormValid],
  );

  const sampleCsvDownloader = (
    <CsvDownloaderHandler type="link" filename={`users-sample`} data={sampleCsv}>
      <Text
        as="span"
        display="inline-flex"
        alignItems="center"
        color="brand.500"
        fontWeight="bold"
        _hover={{ textDecoration: 'underline', color: 'brand.700' }}
      >
        <Trans>CSV template</Trans>
        <Download size={16} style={{ marginLeft: '4px' }} />
      </Text>
    </CsvDownloaderHandler>
  );

  const csvInstructions = (
    <Box as="ol" listStyleType="decimal" pl={4} display="flex" flexDirection="column" gap={2}>
      <Box as="li" key={0} fontSize="sm">
        <Text as="span">
          <Trans>Download the</Trans> {sampleCsvDownloader}{' '}
          <Trans>and add your users details</Trans>
        </Text>
      </Box>
      <Box as="li" key={1} fontSize="sm">
        <Text as="span">
          <Trans>Upload your populated CSV template below</Trans>
        </Text>
      </Box>
      <Box as="li" key={2} fontSize="sm">
        <Text as="span">
          <Trans>Review and confirm the list of users before sending the invites.</Trans>
        </Text>
      </Box>
    </Box>
  );

  return (
    <VStack spacing={4} align="stretch">
      <FormMainContainer>
        <FormContentContainer>
          <VStack spacing={3} alignItems="center" justifyContent="center">
            <Button type="button" onClick={handleCsvUploader} leftIcon={<FilePlus2 size="14px" />}>
              <Trans>CSV Bulk Enrolment</Trans>
            </Button>
            <Text color="brand.500" fontWeight="medium" fontSize="md">
              <Trans>or</Trans>
            </Text>
          </VStack>

          <Box w="full" mb={2}>
            <TeamMemberModeHeader mode={mode} onModeChange={onModeChange} />
          </Box>

          {showCsvUploader ? (
            <CsvImporter
              allowedDomains={allowedDomains}
              validationRules={selectMembersValidationRules}
              columnDisplayNames={selectMembersColumnDisplayNames}
              csvInstructions={csvInstructions}
              onDataProcessed={handleProcessedCsvData}
              onTotalRows={handleTotalRows}
            />
          ) : mode === InternalEnrolmentMode.NEW ? (
            <TeamIndividualUserForm
              ref={formRef}
              member={
                selectedMembers.length > 0
                  ? {
                      id: selectedMembers[0]?.id ?? undefined,
                      firstName: selectedMembers[0]?.firstName ?? '',
                      lastName: selectedMembers[0]?.lastName ?? '',
                      email: selectedMembers[0]?.email ?? '',
                      role: selectedMembers[0]?.role ?? null,
                      providerId: auth?.user.tenant?.id ?? null,
                      site: selectedMembers[0]?.site ?? undefined,
                      region: selectedMembers[0]?.region ?? undefined,
                    }
                  : undefined
              }
              allowRoleSelection={false}
              onFormStateChange={handleIndividualFormStateChange}
            />
          ) : (
            <VStack spacing={3} alignItems="stretch" w="full">
              <Input
                autoComplete="off"
                autoFocus
                fontSize="sm"
                minH="8"
                maxH="8"
                onChange={handleInputChange}
                placeholder="Search existing users"
                size="sm"
                type="text"
                value={search}
                width="full"
              />
              {isLoadingUsers ? (
                <Center h="full" w="full">
                  <Spinner color="border.accent" size="lg" />
                </Center>
              ) : (
                <UserList
                  users={(usersList ?? []).map((user) => ({
                    id: user.id,
                    first_name: user.first_name ?? '',
                    last_name: user.last_name ?? '',
                    email: user.email ?? '',
                    role: ConsultantRoleSchema.safeParse(user?.role).success
                      ? ConsultantRoleSchema.safeParse(user?.role).data
                      : undefined,
                  }))}
                />
              )}
            </VStack>
          )}
        </FormContentContainer>
        <FormFooterContainer>
          <Button
            rightIcon={<ChevronRightIcon />}
            onClick={handleGoToNextStep}
            isLoading={isIndividualFormSubmitting || isIndividualFormLoading || isLoadingUsers}
            isDisabled={!canGoToNextStep}
          >
            <Trans>Next</Trans>
          </Button>
        </FormFooterContainer>
      </FormMainContainer>
    </VStack>
  );
}
